import { Checkbox, Divider } from '@sqs/rosetta-elements';
import { CrossLarge, Search } from '@sqs/rosetta-icons';
import { Box, Button, Flex, Text, Touchable } from '@sqs/rosetta-primitives';
import { useTheme } from '@sqs/rosetta-styled';
import throttle from 'lodash/throttle';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { SEARCH_SELECT_LIMIT } from '../../../const/searchAndSelect';
import { usePlatformBreakpoint } from '../../../hooks/usePlatformBreakpoint';
import { useI18n } from '../../../i18n';

import { PageInfo, PageInfoQueryWithSearch } from '../../../models/PaginatedResponse';
import { SearchSelectItem } from '../../../models/SearchSelectItem';
import { selectEnterprise } from '../../../stores/currentUser';
import { RootState } from '../../../stores/rootReducer';
import { useAppSelector } from '../../../stores/store';
import { preventSubmit } from '../../../utils/keyboard';
import { AcuityLoader } from '../AcuityLoader';
import { AcuityTextField } from '../AcuityTextField';

interface SelectItemForm {
  allItems: boolean;
  items?: SearchSelectItem[];
  totalItems: number;
  checkedItemIds?: number[];
}

interface SearchAndSelectProps {
  readonly keyId?: number;
  readonly searchPlaceholder: string;
  readonly allCheckText: string;
  readonly emptyText: string;
  readonly listItems: (
  enterpriseId: number,
  options?: PageInfoQueryWithSearch)
  => Promise<{data: SearchSelectItem[];pageInfo: PageInfo;}>;
  readonly showVariantSelector?: boolean;
  readonly preloadedData?: SearchSelectItem[];
  readonly providedContainerMaxHeight?: string;
  readonly additionalPaddingBottom?: string;
  readonly preloadedItemStore?: number[];
}

interface LoadAdditionalItemsOptions {
  search?: string;
  replaceData?: boolean;
  page?: number;
}

/**
 * This is a terribly complicated component. This documentation will hopefully explain how to use the component and
 * alleviate some of the confusion.
 *
 * By default, this component is used to display multiple items in a checklist and will attempt to infinitely load
 * checkable elements based on the results of the `listItems` function which should follow a paradigm defined of input
 * and output as defined in the SearchAndSelectProps.
 *
 * This component also offers the ability to search the checkable elements and subsequently store all the checked
 * elements in a local store without disrupting the state of the `react-hook-form` that contains the results.
 *
 * This component also provides two functions for selecting all elements in a list.
 *
 * In the default functionality, if the "Select All" checkbox is checked, it will check all visible elements in the
 * results and disable the scrolling and check functionality of the results and set the total number of selected items
 * as the total number of results from the API.
 *
 * If the `showVariantSelector` option is passed as true, the component will show "Select All" and "Deselect All"
 * buttons that will not modify the functionality of the results but will check all checkable elements visible in the UI
 * at the time and set the total number of selected items as the total number of results from the API.
 *
 * Items can be preloaded from the parent component via the `defaultValue` functionality using `react-hook-form`, which
 * will preset the `fields` const in the `useFormContext` hook. This is different functionality than values being
 * provided via the `preloadedData` prop.
 *
 * @param {string | undefined} additionalPaddingBottom adds additional CSS Padding to the bottom of the component
 * @param {string} allCheckText If the default functionality is used, this is the text label used for the "Select All" checkbox
 * @param {string} emptyText The text displayed when the results return an empty value (initially or on a search)
 * @param {number | undefined} keyId needed in cases where a SearchAnSelect component is rendered multiple times in a block
 * @param {(enterpriseId: number, options?: PageInfoQueryWithSearch) => Promise<{data: SearchSelectItem[], pageInfo: PageInfo}>} listItems The function used to fetch/search results to be output as checkable elements
 * @param {SearchSelectItem[] | undefined} preloadedData Data fetched by the parent component that can be set before fetching more checkable elements
 * @param {string | undefined} providedContainerMaxHeight Used when a container is larger than the default UI for this component
 * @param {string} searchPlaceholder The placeholder for the searchbar in this component
 * @param {boolean | undefined} showVariantSelector Boolean flag for showing the variant UI for this component
 * @param {number[]} preloadedItemStore A preloaded array of IDs of items fetched in advance that should account for the initial total of items "selected"
 * @returns {React.JSX.Element}
 * @constructor
 */
export const SearchAndSelect = ({
  additionalPaddingBottom,
  allCheckText,
  emptyText,
  keyId,
  listItems,
  preloadedData,
  preloadedItemStore = [],
  providedContainerMaxHeight,
  searchPlaceholder,
  showVariantSelector
}: SearchAndSelectProps): React.JSX.Element => {
  const { fontSizes } = useTheme();
  const { isMobile } = usePlatformBreakpoint();
  const { T, t } = useI18n();
  const [searchValue, setSearchValue] = useState<string>('');
  const itemContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLElement>(null);
  const [itemPage, setItemPage] = useState<number>(0);
  const [isFetchingItems, setIsFetchingItems] = useState(false);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [showLoadMoreItems, setShowLoadMoreItems] = useState(false);
  const [lastLoadedItemIndex, setLastLoadedItemIndex] = useState(-1);

  // This item store is necessary to ensure that during searches we persist the values previously checked by the user
  const [itemIdStore, setItemStore] = useState<Set<number>>(new Set(preloadedItemStore));
  const initialRender = useRef(false);

  const searchSelectLimit = providedContainerMaxHeight ? SEARCH_SELECT_LIMIT * 2 : SEARCH_SELECT_LIMIT;

  const { enterpriseId } = useAppSelector((state: RootState) => ({
    enterpriseId: selectEnterprise(state.currentUser)?.id
  }));

  const { control, setValue, watch, register } = useFormContext<SelectItemForm>();

  register('allItems');
  register('totalItems');
  register('checkedItemIds');

  /**
   * using this hook assumes that this component is under a <FormContainer> component and means the `fields` const
   *  *may* be populated by the parent container using the `defaultValues` setup attribute of the `useForm()` hook
   */
  const { fields, append, replace } = useFieldArray({
    control,
    name: 'items'
  });

  const allItems = !!watch('allItems');
  const totalItems = watch('totalItems');
  const hasNoSelectedFields = fields.filter((f) => f.checked).length === 0;

  useEffect(() => {
    setValue('checkedItemIds', Array.from(itemIdStore));
  }, [itemIdStore]);

  const addItemToStore = (id: number) => {
    setItemStore((current) => {
      current.add(id);
      return new Set(current);
    });
  };
  const removeItemFromStore = (id: number) => {
    setItemStore((current) => {
      current.delete(id);
      return new Set(current);
    });
  };

  const renderClearButton = useMemo(() => {
    if (!searchValue) {
      return null;
    }
    return (
      <Touchable.Element.Icon
        type="button"
        onClick={() => {
          setSearchValue('');
        }}>

        <CrossLarge />
      </Touchable.Element.Icon>);

  }, [searchValue]);

  /**
   * fetches data from the provided `listItems` function. by default, items will be appended unless the `replaceData`
   *  param is true (e.g. when fetching search results)
   * @param {PageInfoQueryWithSearch} pageInfo
   * @param {boolean} replaceData
   */
  const fetchItems = (pageInfo: PageInfoQueryWithSearch = {}, replaceData?: boolean) => {
    if (enterpriseId) {
      setIsFetchingItems(true);
      setShowLoadMoreItems(false);
      listItems(enterpriseId, pageInfo).then((res) => {
        const itemsFetched = res.data.map((item) => ({
          ...item,
          checked: itemIdStore.has(item.itemId)
        }));
        if (replaceData) {
          replace(itemsFetched);
        } else {
          append(itemsFetched);
        }
        setValue('totalItems', res.pageInfo.total);
        setIsFetchingItems(false);
      });
    }
  };

  /**
   * used during scroll to pull more items from the backend
   */
  const loadAdditionalItems = throttle(({ search, page, replaceData }: LoadAdditionalItemsOptions = {}) => {
    if (!isFetchingItems) {
      const newPage = page || 0;
      const limit = SEARCH_SELECT_LIMIT;
      const offset = newPage * limit;
      const options: PageInfoQueryWithSearch = { limit, offset };
      if (search) {
        options.search = search;
      }
      setItemPage(newPage);
      fetchItems(options, replaceData);
    }
  }, 300);

  /**
   * Only trigger a search after a 500ms timeout of not typing
   * When we search, make sure the itemPage is 0 to ensure we get the first page of results
   * TODO: This can be improved upon for items where results are slow to appear and typing is at a slow rate
   */
  useEffect(() => {
    if (initialRender.current) {
      const searchTimeout = setTimeout(() => {
        setLastScrollTop(0);
        loadAdditionalItems({ search: searchValue, replaceData: true });
      }, 500);
      return () => {
        clearTimeout(searchTimeout);
      };
    }

    initialRender.current = true;
  }, [searchValue]);

  /**
   * Initialization useEffect to either load preloaded data into the `fields` of the form or to fetch items from the API
   * call provided (`listItems`). In the case where data is already present (via defaultValues initialization in the
   * parent component) and there are checked values in the dataset, we want to ensure they STAY checked after this
   * component loads, so we add them to the itemStore.
   */
  useEffect(() => {
    if (hasNoSelectedFields) {
      if (preloadedData && preloadedData.length > 0) {
        append(preloadedData);
      } else {
        fetchItems({ limit: searchSelectLimit, offset: 0 }, true);
      }
    } else {
      fields.forEach((field) => {
        if (field.checked) {
          addItemToStore(field.itemId);
        }
      });
    }
  }, [keyId]);

  const selectAllAction = () => {
    setValue('allItems', true, { shouldDirty: true });
    fields.forEach((f, i) => {
      setValue(`items.${i}.checked`, true, { shouldDirty: true });

      if (showVariantSelector) {
        addItemToStore(f.itemId);
      }
    });
  };

  const deselectAllAction = () => {
    setValue('allItems', false, { shouldDirty: true });
    if (showVariantSelector) {
      [...itemIdStore].forEach((item) => {
        removeItemFromStore(item);
      });
    }
    fields.forEach((f, i) => {
      setValue(`items.${i}.checked`, false, { shouldDirty: true });
    });
  };

  /**
   * This useEffect is only used with the default functionality, and we are not loading preselected data (from a
   * permissions fetch)
   */
  useEffect(() => {
    if (!showVariantSelector && hasNoSelectedFields) {
      if (allItems) {
        selectAllAction();
      } else {
        deselectAllAction();
      }
    }
  }, [allItems, fields]);

  /**
   * This useEffect is used to focus the first newly loaded item when a using keyboard navigation and has triggered
   * the Load More button
   */
  useEffect(() => {
    if (fields.length && lastLoadedItemIndex > -1 && fields[lastLoadedItemIndex + 1]) {
      const fieldItem = fields[lastLoadedItemIndex + 1];
      const focusElement = itemContainerRef.current?.querySelector(`#item-${fieldItem.id}`) as HTMLElement | null;
      focusElement?.focus();
      focusElement?.scrollIntoView();
    }
  }, [fields]);

  /**
   * Load more items when we reach the bottom of the item list. If the user tries to scroll up, return early.
   */
  const scrollLoadMoreItems = throttle(() => {
    const itemContainer = itemContainerRef.current;

    // if user is focusing on elements with a keyboard, show the load button (for a11y users)
    if (itemContainer?.contains(document.activeElement) && fields.length < totalItems) {
      setShowLoadMoreItems(true);
    }

    if (itemContainer && fields.length < totalItems && !allItems) {
      // if the user scrolls up, don't do anything
      if (itemContainer.scrollTop < lastScrollTop) {
        return;
      }
      setLastScrollTop(itemContainer.scrollTop <= 0 ? 0 : itemContainer.scrollTop);
      if (itemContainer.scrollTop + itemContainer.offsetHeight >= itemContainer.scrollHeight) {
        loadAdditionalItems({ page: itemPage + 1 });
      }
    }
  }, 500);

  /**
   * Trigger loading more items via the Keyboard Navigation Load More button
   */
  const focusedLoadMoreItems = () => {
    if (fields.length < totalItems) {
      setLastLoadedItemIndex(fields.length - 1);
      loadAdditionalItems({ search: searchValue, page: itemPage + 1 });
    }
  };

  const getTotalItems = (): number => {
    return allItems ? totalItems : itemIdStore.size;
  };

  const containerMaxHeight = providedContainerMaxHeight ? providedContainerMaxHeight : '';
  const itemsBoxPaddingBottom = additionalPaddingBottom ? additionalPaddingBottom : 3;
  return (
    <Flex flexDirection="column" maxHeight={containerMaxHeight}>
      <AcuityTextField
        inputProps={{
          placeholder: searchPlaceholder,
          onChange: setSearchValue,
          onKeyDown: preventSubmit,
          value: searchValue,
          fontSize: fontSizes[4],
          disabled: totalItems === 0
        }}
        interiorPost={renderClearButton}
        interiorPre={<Search />} />

      {showVariantSelector ?
      <Flex my={2} justifyContent="space-between" alignItems="center">
          <Flex flexGrow={2}>
            <Text.Caption fontWeight={500} fontSize={fontSizes[3]}>
              {t("{items} items selected", { items: `${getTotalItems()}` }, { project: 'enterprise-dashboard' })}
            </Text.Caption>
          </Flex>
          <Flex>
            <Button.Tertiary color="black" mr={8} size="medium" onClick={selectAllAction}>
              <T project="enterprise-dashboard">{"Select all"}</T>
            </Button.Tertiary>
            <Button.Tertiary color="black" size="medium" onClick={deselectAllAction}>
              <T project="enterprise-dashboard">{"Deselect all"}</T>
            </Button.Tertiary>
          </Flex>
        </Flex> :

      <Flex justifyContent="space-between" alignItems="flex-end" mb={2}>
          <Text.Body as="label" css={{ display: 'inline-flex' }} mt={2}>
            <Checkbox
            name="allItems"
            onChange={(v: boolean) => {
              setValue('allItems', v, { shouldDirty: true });
            }}
            checked={allItems}
            value={true}
            mr={2} />

            {allCheckText}
          </Text.Body>
          {itemIdStore.size > 0 || allItems ?
        <Text.Caption fontWeight={500}>
              {t("{items} selected", { items: `${getTotalItems()}` }, { project: 'enterprise-dashboard' })}
            </Text.Caption> :
        null}
        </Flex>
      }
      <Divider />
      {totalItems > 0 ?
      <Box
        ref={itemContainerRef}
        mt={2}
        minHeight="300px"
        maxHeight={containerMaxHeight ? '' : '300px'}
        overflowY={!showVariantSelector && allItems ? 'hidden' : 'scroll'}
        onScroll={scrollLoadMoreItems}
        pb={itemsBoxPaddingBottom}>

          {fields.map((item, i) => {
          const isChecked = watch(`items.${i}.checked`) || itemIdStore.has(item.itemId) || allItems;

          const isDisabled = () => {
            if (showVariantSelector) {
              return isFetchingItems;
            }
            return allItems || isFetchingItems;
          };

          const textColor = isDisabled() ? 'gray.600' : '';
          return (
            <div key={item.id}>
                <Text.Body as="label" display="inline-flex" color={textColor} mt={2}>
                  <Checkbox
                  id={`item-${item.id}`}
                  name="itemList"
                  onChange={(v: boolean) => {
                    if (!isDisabled() && !v) {
                      setValue('allItems', false, { shouldDirty: true });
                    }
                    setValue(`items.${i}.checked`, v, { shouldDirty: true });
                    if (v) {
                      addItemToStore(item.itemId);
                    } else {
                      removeItemFromStore(item.itemId);
                    }
                  }}
                  isDisabled={isDisabled()}
                  checked={isChecked}
                  value={item.id}
                  mr={3} />

                  {item.name}
                </Text.Body>
              </div>);

        })}
          {showLoadMoreItems &&
        <Box sx={{ textAlign: 'center' }}>
              <Button.Tertiary ref={loadMoreRef} width="90%" type="button" onClick={focusedLoadMoreItems}>
                <T project="enterprise-dashboard">{"Load more"}</T>
              </Button.Tertiary>
            </Box>
        }
        </Box> :

      <Text.Body fontStyle="italic">{emptyText}</Text.Body>
      }
      {isFetchingItems &&
      <Box position="absolute" bottom="15px" left="50%" translate="-50%">
          <AcuityLoader />
        </Box>
      }
      {!isMobile && <Divider />}
    </Flex>);

};